import { kAgentAssistantRouterApiBasePath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'
import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { UserFileReferenceViewerResponse, UserFileReferenceViewerResponseSchema } from '../schema/userFileReferenceViewer.schema'

export async function getUserFileReferenceForStandaloneViewer(referenceId: string, matterId: string): Promise<UserFileReferenceViewerResponse> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    const apiUrl = new URL(`${kPaxtonAppApiRESTBaseUrl()}${kAgentAssistantRouterApiBasePath}/references/${encodeURIComponent(referenceId)}/file-viewer`)
    apiUrl.searchParams.set('matter_id', matterId)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl.toString(), requestOptions)

    if (!response.ok) {
      let errorDetail = response.statusText
      try {
        const errorData = await response.json()
        errorDetail = JSON.stringify(errorData.detail || errorData)
      } catch {
        // Response is not JSON; keep the status text.
      }

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`User-file reference viewer fetch error - status: ${response.status}, detail: ${errorDetail}`), {
          extra: {
            requestUrl: apiUrl.toString(),
            responseStatus: response.status,
            responseStatusText: response.statusText,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            referenceId,
            matterId,
          },
        })
      })
      throw new ApiError(response.status, `User-file reference viewer fetch error - status: ${response.status}, detail: ${errorDetail}`)
    }

    const data = await response.json()
    return UserFileReferenceViewerResponseSchema.parse(data)
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, referenceId, matterId },
        })
      })
    }
    throw error
  }
}
